import { FormEvent } from "react";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import styled from "styled-components";
import { addPaymentCard, openNotification } from "@dataone/lib";
import { UseQueryResult, useMutation } from "@tanstack/react-query";
import { CalendarIcon, CreditCardIcon, LockIcon } from "@dataone/icons";

interface CheckoutFormProps {
  handleCancel?: () => void;
  refetchCards: (options?: {
    throwOnError: boolean;
    cancelRefetch: boolean;
  }) => Promise<UseQueryResult>;
}

const CheckoutForm = ({ refetchCards, handleCancel }: CheckoutFormProps) => {
  const stripe = useStripe();
  const elements = useElements();

  const { mutate: addCard, isLoading: isPaymentCardLoading } = useMutation({
    mutationFn: addPaymentCard,
  });

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements)
      return openNotification(
        "Card can't be added at the moment, please try again later",
        "error"
      );
    const cardElement = elements.getElement(CardNumberElement);
    if (cardElement) {
      const { error, token } = await stripe.createToken(cardElement);
      if (error?.message) return openNotification(error?.message, "error");
      if (token)
        addCard(token.id, {
          onSuccess: ({ message }) => {
            openNotification(message, "success");
            handleCancel?.();
            elements.getElement(CardNumberElement)?.clear();
            elements.getElement(CardExpiryElement)?.clear();
            elements.getElement(CardCvcElement)?.clear();
            refetchCards();
          },
        });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: "25px" }}>
        <StyledLabel>
          Credit card number
          <div>
            <StyledInput>
              <StyledCreditCardIcon>
                <CreditCardIcon />
              </StyledCreditCardIcon>
              <div style={{ flex: "1" }}>
                <CardNumberElement options={CARD_ELEMENT_OPTION1} />
              </div>
            </StyledInput>
          </div>
        </StyledLabel>
      </div>
      <div style={{ display: "flex", gap: "80px" }}>
        <div style={{ flex: "1" }}>
          <StyledLabel>
            Expiration date
            <StyledInput>
              <StyledCalendarIcon>
                <CalendarIcon />
              </StyledCalendarIcon>
              <div style={{ flex: "1" }}>
                <CardExpiryElement options={CARD_ELEMENT_OPTION2} />
              </div>
            </StyledInput>
          </StyledLabel>
        </div>
        <div style={{ flex: "1" }}>
          <StyledLabel>
            CVC
            <StyledInput>
              <StyledLockIcon>
                <LockIcon />
              </StyledLockIcon>
              <div style={{ flex: "1" }}>
                <CardCvcElement options={CARD_ELEMENT_OPTION3} />
              </div>
            </StyledInput>
          </StyledLabel>
        </div>
      </div>
      <StyledBtn
        type="submit"
        disabled={!stripe || !elements || isPaymentCardLoading}
      >
        {isPaymentCardLoading ? "Adding card ..." : "Add card"}
      </StyledBtn>
    </form>
  );
};

export default CheckoutForm;
const CARD_ELEMENT_OPTION3 = {
  style: {
    base: {
      color: "#32325d",
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "#aab7c4",
        fontSize: "12px",
      },
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  },
  placeholder: "XXX",
};
const CARD_ELEMENT_OPTION2 = {
  style: {
    base: {
      color: "#32325d",
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "#aab7c4",
        fontSize: "12px",
      },
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  },
  placeholder: "mm/yy",
};
const CARD_ELEMENT_OPTION1 = {
  style: {
    base: {
      color: "#32325d",
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "#aab7c4",
        fontSize: "12px",
      },
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  },
  placeholder: "XXXX   XXXX   XXXX   XXXX",
};

const StyledInput = styled.div`
  background: #eff1fd;
  border-radius: 6px;
  padding: 10px 10px;
  margin: 10px 0;
  display: flex;
  gap: 15px;
`;
const StyledLabel = styled.label`
  font-size: 13px;
  color: #040726;
  font-weight: 700;
  span {
    color: red;
  }
`;

const StyledBtn = styled.button`
  border-radius: 6px;
  background: #040726 !important;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25) inset;
  width: 100%;
  height: 35px;
  flex-shrink: 0;
  color: #eff1fd !important;
  font-weight: 600;
  font-size: 12px;
  border: 0;
  margin-top: 20px;
  cursor: pointer;
`;

const StyledCreditCardIcon = styled.div`
  display: flex;
  font-size: 20px;
  color: #000;
`;

const StyledCalendarIcon = styled.div`
  display: flex;
  font-size: 19px;
  color: #000;
`;

const StyledLockIcon = styled.div`
  display: flex;
  font-size: 20px;
  color: #000;
`;
